import { useEffect, useState, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import { setCurrentChannel, setCurrentDepartment, setSelectedChannel as setStoreSelectedChannel, setSelectedDepartment as setStoreSelectedDepartment } from '@/store/slices/analytics';
import { fetchAllDepartments } from '@/store/slices/departments';
import { UserRole } from '@/api/auth/types';
import api from '@/api';

interface ChannelOption {
  value: number;
  label: string;
}

interface DepartmentOption {
  value: string;
  label: string;
}

export const useAnalyticsFilters = (isActive: boolean = true) => {
  const dispatch = useAppDispatch();
  const { departments } = useAppSelector(state => state.departments);
  const { currentChannel, currentDepartment, selectedChannel: storeSelectedChannel, selectedDepartment: storeSelectedDepartment } = useAppSelector(state => state.analytics);

  const channelId = localStorage.getItem("channel_id");
  const departmentId = localStorage.getItem("department_id");
  const userRole = localStorage.getItem("role") as UserRole;

  // Функция для проверки, должно ли подразделение предзаполняться для данной роли
  const shouldPrefilDepartment = useCallback(() => {
    return userRole !== UserRole.ACCOUNT_MANAGER && userRole !== UserRole.DELIVERY_MANAGER;
  }, [userRole]);

  // Состояния для списков опций
  const [channelList, setChannelList] = useState<ChannelOption[]>([]);
  const [departmentList, setDepartmentList] = useState<DepartmentOption[]>([]);

  // Состояние для выбранного подразделения с localStorage
  const [selectedDepartment, setSelectedDepartment] = useState<DepartmentOption | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<ChannelOption | null>(null);
  
  // Флаг для отслеживания инициализации
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Флаг для отслеживания изменения канала на "Все"
  const [shouldResetToAllDepartments, setShouldResetToAllDepartments] = useState(false);

  // Функция для загрузки подразделений для конкретного канала
  const loadDepartmentsForChannel = useCallback(async (channelValue: number, isInitialLoad: boolean = false) => {
    try {
      if (channelValue === 0) {
        // Если выбран канал "Все", загружаем все подразделения
        if (!isInitialLoad) {
          setShouldResetToAllDepartments(true);
        }
        await dispatch(fetchAllDepartments());
        // Обработка будет в useEffect для departments
      } else {
        // Загружаем подразделения для конкретного канала
        const res = await api.departments.departments({
          params: {
            channel_id: channelValue,
          },
        }) as any;
        
        const newDepartmentList = res.items.map((department: any) => ({
          value: String(department.id),
          label: department.name,
        }));
        newDepartmentList.unshift({ value: "all", label: "Все" });
        
        setDepartmentList(newDepartmentList);
        
        if (isInitialLoad) {
          // При инициализации пытаемся восстановить сохраненное подразделение
          let initialDepartment;
          
          if (shouldPrefilDepartment()) {
            // Для ролей, которым разрешено предзаполнение
            if (storeSelectedDepartment !== null) {
              initialDepartment = newDepartmentList.find((department: any) => department.value === storeSelectedDepartment) || newDepartmentList[0];
            } else {
              const departmentIdFromStorage = departmentId === "null" ? "all" : departmentId;
              initialDepartment = newDepartmentList.find((department: any) => department.value === departmentIdFromStorage) || newDepartmentList[0];
              dispatch(setStoreSelectedDepartment(departmentIdFromStorage));
            }
          } else {
            // Для ролей account_manager и delivery_manager - всегда "Все"
            initialDepartment = newDepartmentList[0]; // "Все" всегда первый элемент
            dispatch(setStoreSelectedDepartment("all"));
          }
          
          setSelectedDepartment(initialDepartment);
          dispatch(setCurrentDepartment(Number(initialDepartment.value === "all" ? 0 : initialDepartment.value)));
          setIsInitialized(true);
        } else {
          // При обычном изменении канала сбрасываем на "Все"
          const allDepartment = {value: "all", label: "Все"};
          setSelectedDepartment(allDepartment);
          dispatch(setCurrentDepartment(0));
          dispatch(setStoreSelectedDepartment("all"));
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки подразделений для канала:', error);
    }
  }, [dispatch, storeSelectedDepartment, departmentId, setSelectedDepartment, setDepartmentList, setIsInitialized, setShouldResetToAllDepartments, shouldPrefilDepartment]);

  // Загрузка каналов
  useEffect(() => {
    if (isActive) {
      (async () => {
        try {
          const { data } = await api.channel.getChannels();
          const channels = data.map((channel) => ({
            value: channel.id,
            label: channel.name,
          }));
          channels.unshift({ value: 0, label: "Все" });
          setChannelList(channels);
        } catch (error) {
          console.error('Ошибка загрузки каналов:', error);
        }
      })();
    }
  }, [isActive]);

  // Загрузка подразделений
  useEffect(() => {
    if (isActive) {
      dispatch(fetchAllDepartments());
    }
  }, [dispatch, isActive]);


  // Обработка изменения подразделения
  const handleDepartmentChange = (value: DepartmentOption | null) => {
    if (value) {
      setSelectedDepartment(value);
      dispatch(setStoreSelectedDepartment(value.value));
    }
  };

  // Синхронизация выбранного подразделения со store
  useEffect(() => {
    if (selectedDepartment && selectedDepartment.value !== "all") {
      dispatch(setCurrentDepartment(Number(selectedDepartment.value)));
    } else if (selectedDepartment && selectedDepartment.value === "all") {
      dispatch(setCurrentDepartment(0));
    }
  }, [selectedDepartment, dispatch]);

  // Инициализация каналов
  useEffect(() => {
    if (channelList.length > 0 && selectedChannel === null && !isInitialized) {
      let initialChannel;
      if (storeSelectedChannel !== null) {
        // Используем сохраненное значение из store
        initialChannel = channelList.find((channel) => channel.value === Number(storeSelectedChannel)) || channelList[0];
      } else {
        // Если в store нет значения, используем localStorage
        const channelIdFromStorage = Number(channelId === "null" ? 0 : channelId);
        initialChannel = channelList.find((channel) => channel.value === channelIdFromStorage) || channelList[0];
        dispatch(setStoreSelectedChannel(String(channelIdFromStorage)));
      }
      setSelectedChannel(initialChannel);
      dispatch(setCurrentChannel(initialChannel.value));
      
      // Загружаем подразделения для выбранного канала при инициализации
      loadDepartmentsForChannel(initialChannel.value, true);
    }
  }, [channelList, channelId, dispatch, selectedChannel, storeSelectedChannel, isInitialized, loadDepartmentsForChannel]);

  // Обработка подразделений для канала "Все"
  useEffect(() => {
    if (departments && selectedChannel && selectedChannel.value === 0) {
      const resultDepartments = departments.items.map((el) => ({ 
        value: String(el.id), 
        label: el.name 
      }));
      resultDepartments.unshift({ value: "all", label: "Все" });
      setDepartmentList(resultDepartments);
      
      if (!isInitialized) {
        // Инициализация при первой загрузке
        let initialDepartment;
        
        if (shouldPrefilDepartment()) {
          // Для ролей, которым разрешено предзаполнение
          if (storeSelectedDepartment !== null) {
            initialDepartment = resultDepartments.find((department) => department.value === storeSelectedDepartment) || resultDepartments[0];
          } else {
            const departmentIdFromStorage = departmentId === "null" ? "all" : departmentId;
            initialDepartment = resultDepartments.find((department) => department.value === departmentIdFromStorage) || resultDepartments[0];
            dispatch(setStoreSelectedDepartment(departmentIdFromStorage));
          }
        } else {
          // Для ролей account_manager и delivery_manager - всегда "Все"
          initialDepartment = resultDepartments[0]; // "Все" всегда первый элемент
          dispatch(setStoreSelectedDepartment("all"));
        }
        
        setSelectedDepartment(initialDepartment);
        dispatch(setCurrentDepartment(Number(initialDepartment.value === "all" ? 0 : initialDepartment.value)));
        setIsInitialized(true);
      } else if (shouldResetToAllDepartments) {
        // Сброс при изменении канала на "Все"
        const allDepartment = {value: "all", label: "Все"};
        setSelectedDepartment(allDepartment);
        dispatch(setCurrentDepartment(0));
        dispatch(setStoreSelectedDepartment("all"));
        setShouldResetToAllDepartments(false);
      }
    }
  }, [departments, selectedChannel, departmentId, dispatch, storeSelectedDepartment, isInitialized, shouldResetToAllDepartments, shouldPrefilDepartment]);

  // Обработка изменения канала
  const handleChannelChange = async (value: ChannelOption | null) => {
    if (value) {
      setSelectedChannel(value);
      dispatch(setCurrentChannel(value.value === 0 ? 0 : value.value));
      dispatch(setStoreSelectedChannel(String(value.value)));
      
      // Загружаем подразделения для выбранного канала (не инициализация)
      await loadDepartmentsForChannel(value.value, false);
    }
  };

  return {
    // Данные для каналов
    channelList,
    currentChannelValue: selectedChannel,
    handleChannelChange,

    // Данные для подразделений
    departmentList,
    currentDepartmentValue: selectedDepartment,
    handleDepartmentChange,

    // Дополнительные данные
    currentChannel,
    currentDepartment,
    selectedDepartment,
  };
};
